import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { CopyIcon, PlusIcon, Search } from "lucide-react";
import type {
  EquipmentModelId,
  FermentationDashboardId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { useZodForm } from "@/components/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/date";

export const Route = createFileRoute("/fermentations/dashboards/")({
  component: DashboardLibrary,
});

const newDashboardSchema = z.object({
  name: z.string().min(1, "Name is required"),
  equipmentModelId: z.coerce
    .number()
    .transform((value) => value as EquipmentModelId)
    .optional(),
});

function DashboardLibrary() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const canEdit = useCan("fermentation-dashboard:edit");
  const [searchQuery, setSearchQuery] = useState("");
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const { data: dashboards, isLoading } =
    trpc.fermentationDashboards.list.useQuery({});
  const { data: models } = trpc.equipment.fermentors.models.list.useQuery();
  const modelName = (id: EquipmentModelId | null) =>
    id === null ? null : (models?.find((m) => m.id === id)?.name ?? "Unknown");

  const create = trpc.fermentationDashboards.create.useMutation({
    onSuccess: (id, input) => {
      toast.success(
        input.copiedFromRevisionId
          ? `Copied to "${input.name}"`
          : `Dashboard "${input.name}" created`,
      );
      void utils.fermentationDashboards.list.invalidate();
      setIsNewDialogOpen(false);
      void navigate({
        to: "/fermentations/dashboards/$dashboardId",
        params: { dashboardId: String(id) },
      });
    },
    onError: (error) => toast.error(`Failed to create: ${error.message}`),
  });
  const remove = trpc.fermentationDashboards.delete.useMutation({
    onSuccess: () => {
      toast.success("Dashboard deleted.");
      void utils.fermentationDashboards.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const form = useZodForm({
    schema: newDashboardSchema,
    defaultValues: { name: "" },
  });
  const handleCreate = form.handleSubmit((values) => {
    create.mutate({
      name: values.name,
      equipmentModelId: values.equipmentModelId ?? null,
    });
  });

  const shown = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (dashboards ?? []).filter(
      (d) =>
        !query ||
        d.name.toLowerCase().includes(query) ||
        (modelName(d.equipment_model_id) ?? "").toLowerCase().includes(query),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboards, searchQuery, models]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">Loading dashboards...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search dashboards..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {canEdit && (
          <Dialog open={isNewDialogOpen} onOpenChange={setIsNewDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon className="w-4 h-4 mr-1" />
                New Dashboard
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Dashboard</DialogTitle>
                <DialogDescription>
                  A shared operating view. Procedures choose a default;
                  individual fermentations can select a variant.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., DASGIP fed-batch"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="equipmentModel">Equipment model</Label>
                    <Select
                      value={form.watch("equipmentModelId")?.toString() ?? ""}
                      onValueChange={(value) =>
                        form.setValue(
                          "equipmentModelId",
                          value.trim()
                            ? (parseInt(value) as EquipmentModelId)
                            : undefined,
                        )
                      }
                    >
                      <SelectTrigger id="equipmentModel">
                        <SelectValue placeholder="Select model (optional, can be assigned later)" />
                      </SelectTrigger>
                      <SelectContent>
                        {models?.map((model) => (
                          <SelectItem key={model.id} value={model.id.toString()}>
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The model's wiring is what the editor previews against and
                      validates bindings for.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsNewDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending}>
                    {create.isPending ? "Creating..." : "Create Dashboard"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="divide-y divide-border rounded-md border">
        {shown.map((dashboard) => {
          const uses = dashboard.procedure_uses + dashboard.fermentation_uses;
          return (
            <div
              key={dashboard.id}
              className="group flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link
                    to="/fermentations/dashboards/$dashboardId"
                    params={{ dashboardId: String(dashboard.id) }}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {dashboard.name}
                  </Link>
                  {dashboard.current_revision !== null && (
                    <Badge variant="outline">
                      rev {dashboard.current_revision}
                    </Badge>
                  )}
                  {dashboard.copied_from_revision_id && (
                    <Badge variant="secondary">Variant</Badge>
                  )}
                  {dashboard.equipment_model_id !== null ? (
                    <Badge variant="outline" className="text-muted-foreground">
                      {modelName(dashboard.equipment_model_id)}
                    </Badge>
                  ) : (
                    <Badge variant="warning">No equipment model</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span>
                    Updated {formatDate(dashboard.updated_at)}
                    {dashboard.last_updated_by_name
                      ? ` by ${dashboard.last_updated_by_name}`
                      : ""}
                  </span>
                  <span>
                    {uses === 0
                      ? "Not in use"
                      : [
                          dashboard.procedure_uses > 0 &&
                            `${dashboard.procedure_uses} ${dashboard.procedure_uses === 1 ? "procedure" : "procedures"}`,
                          dashboard.fermentation_uses > 0 &&
                            `${dashboard.fermentation_uses} ${dashboard.fermentation_uses === 1 ? "fermentation" : "fermentations"}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                  </span>
                </div>
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Duplicate"
                    disabled={create.isPending || !dashboard.current_revision_id}
                    onClick={() =>
                      create.mutate({
                        name: `${dashboard.name} — copy`,
                        equipmentModelId: dashboard.equipment_model_id,
                        copiedFromRevisionId: dashboard.current_revision_id!,
                      })
                    }
                  >
                    <CopyIcon className="h-3 w-3" />
                  </Button>
                  <DeleteConfirmDialog
                    title="Delete Dashboard"
                    description={`Delete "${dashboard.name}" and all its revisions? This cannot be undone.`}
                    onConfirm={() =>
                      remove.mutate(dashboard.id as FermentationDashboardId)
                    }
                    isDeleting={
                      remove.isPending && remove.variables === dashboard.id
                    }
                    disabled={uses > 0}
                  />
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-3 text-sm">
              {searchQuery
                ? "No dashboards found matching your search."
                : "No dashboards yet. Create your first one!"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
